/*
 @author: M.Wang
 Created by preference on 2023/01/09
*/

import { sectionMixin } from '@/assets/vue/mixins/section'
/**
 * 创建区块
 * @author M.Wang
 * @date 2023-01-09
 * @param {ComponentOptions<Vue>} options?:ComponentOptions<Vue>
 * @returns {any}
 */
let idx = 0
export function createSection(options) {
  const mixins = options.mixins || []
  mixins.push(sectionMixin(idx++))
  options.mixins = mixins
  return options
}

/**
 * 获取滚动条高
 * @author M.Wang
 * @date 2023-01-14
 * @returns {any}
 */
export function getScrollTop() {
  let r = 0
  if (document.documentElement && document.documentElement.scrollTop) {
    r = document.documentElement.scrollTop
  } else if (document.body) {
    r = document.body.scrollTop
  }
  return r
}

/**
 * 防抖函数
 * fn(函数， 时间ms=1000)
 */
export function debounce(fn, delay = 1000) {
  let timer

  return function () {
    if (timer) {
      clearTimeout(timer)
    }

    timer = setTimeout(() => {
      fn.apply(this, arguments)
      timer = null
    }, delay)
  }
}

/**
 * 视频在可视窗口时播放视频
 * fn(video标签dom, 显示区域=0.5, needPause = 是否需要自动暂停功能，默认true)
 */
export function videoPlayByVisibleArea(dom, areaRate = 0.5, needPause = true) {
  if (!dom) return
  const loop = dom.loop

  // 标志位，用于记录当前播放状态
  let isPlaying = false
  const callback = (entries) => {
    const entry = entries[0]
    if (entry?.intersectionRatio >= areaRate) {
      // 如果视频未播放，则调用 play()
      if (!isPlaying) {
        dom
          .play()
          .then(() => {
            isPlaying = true // 更新状态为播放中
          })
          .catch((error) => {
            console.error('播放失败:', error)
          })
      }
    } else {
      // 如果需要暂停且视频正在播放，则调用 pause()
      if (needPause && isPlaying) {
        dom.pause()
        isPlaying = false // 更新状态为暂停
      }
    }
  }

  const io = new IntersectionObserver(callback, {
    delay: 0,
    threshold: areaRate,
  })
  io.observe(dom)

  !loop &&
    dom.addEventListener('ended', () => {
      io.disconnect()
    })
}

/**
 * 视频在可视窗口时播放视频，h5兼容代码
 * fn(video标签dom, 显示区域=0.5, needPause = 是否需要自动暂停功能，默认true)
 */
export function videoPlayByVisibleAreaH5(
  dom,
  areaRate = 0.5,
  needPause = true,
) {
  const loop = dom.loop

  const callback = (e) => {
    if (e?.[0]?.intersectionRatio >= areaRate) {
      const func = () => {
        dom.play()
        document.removeEventListener('click', func)
      }

      dom.play()
      document.addEventListener('click', func)
    } else {
      needPause && dom.pause()
    }
  }

  const io = new IntersectionObserver(callback, {
    delay: 0,
    threshold: areaRate,
  })
  io.observe(dom)

  !loop &&
    dom.addEventListener('ended', () => {
      io.disconnect()
    })
}

/**
 * 视频在可视窗口时播放视频，h5兼容代码
 * fn(video标签dom, 显示区域=0.5, needPause = 是否需要自动暂停功能，默认true)
 */
export function videoReplayByVisibleArea(dom, areaRate = 0.5) {
  const callback = (e) => {
    if (e?.[0]?.intersectionRatio >= areaRate) {
      if (dom.ended || dom.currentTime === 0) {
        replayVideo(dom)
      }
    }
  }

  const io = new IntersectionObserver(callback, {
    threshold: areaRate,
  })
  io.observe(dom)
}

/**
 * dom在可视窗口时调用回调函数
 * fn(video标签dom, 显示区域=0.5, callback=() => {}, disconnect = true：结束时，是否注销observer)
 */
export function domVisibleArea(
  dom,
  areaRate = 0.5,
  callback = () => {},
  disconnect = true,
) {
  const io = new IntersectionObserver(
    function (e, observer) {
      if (e?.[0]?.intersectionRatio >= areaRate) {
        callback()

        if (disconnect) {
          observer.disconnect()
        }
      }
    },
    { delay: 0, threshold: areaRate },
  )

  io.observe(dom)
}

/**
 * dom在可视窗口时调用回调函数，并判断进入方向
 * @param {HTMLElement} dom - 需要观察的 DOM 元素
 * @param {number} areaRate - 显示区域的比例，默认 0.5
 * @param {Function} callback - 回调函数，参数为方向 ('down' 或 'up')
 * @param {boolean} disconnect - 是否在触发后断开观察器，默认 true
 */
export function domVisibleArea2Direction(
  dom,
  callback = (direction) => {},
  areaRate = 0.5,
  threshold = [areaRate],
  disconnect = false,
) {
  let lastY = 0 // 记录上一次的 Y 坐标

  const io = new IntersectionObserver(
    function (entries, observer) {
      entries.forEach((entry) => {
        const { top, bottom } = entry.boundingClientRect
        const viewportHeight =
          window.innerHeight || document.documentElement.clientHeight
        if (entry.intersectionRatio >= areaRate) {
          const direction = top > lastY ? 'down' : 'up' // 判断方向
          lastY = top // 更新上一次的 Y 坐标
          callback(direction) // 调用回调函数，传入方向
        } else if (bottom < 0) {
          callback('above') // 元素在视口上方
        } else if (top > viewportHeight) {
          callback('below') // 元素在视口下方
        }

        if (disconnect) {
          observer.disconnect()
        }
      })
    },
    { threshold },
  )

  io.observe(dom)
}

export function isUCBrowser() {
  return /UCBrowser/.test(navigator.userAgent)
}

export function replayVideo(video) {
  if (video) {
    isUCBrowser() && dom.pause()
    video.currentTime = 0
    video.play()
  }
}

export function matchMedia() {
  let width = 880
  watchMedia('(min-width: 1680px)', (matches) => {
    if (matches) {
      width = 880
    }
  })
  watchMedia('(min-width: 1920px)', (matches) => {
    if (matches) {
      width = 1000
    }
  })
  watchMedia('(min-width: 2560px)', (matches) => {
    if (matches) {
      width = 1200
    }
  })
  return width
}

/**
 * 监听媒体查询变化
 * @param {string} query - 媒体查询字符串
 * @param {Function} callback - 媒体查询状态变化时的回调函数
 * @returns {Function} - 返回清理函数，用于移除监听器
 */
export function watchMedia(query, callback) {
  const mediaQuery = window.matchMedia(query)

  const handler = (e) => {
    callback(e.matches)
  }

  mediaQuery.addEventListener('change', handler)

  // 立即执行一次回调，获取初始状态
  callback(mediaQuery.matches)

  // 返回清理函数
  return () => {
    mediaQuery.removeEventListener('change', handler)
  }
}
